import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Code2, Layers, Cloud, Database, Wrench } from 'lucide-react';

export const Skills = () => {
  const skillCategories = [
    {
      id: 'backend',
      name: 'Backend',
      icon: Code2,
      color: 'text-primary',
      skills: [
        '.NET Core', 'EF Core', 'Blazor', 'C#', 'ASP.NET',
        'Web API', 'MVC', 'LINQ', 'ADO.NET', 'WCF'
      ]
    },
    {
      id: 'frontend',
      name: 'Frontend',
      icon: Layers,
      color: 'text-accent',
      skills: [
        'Angular 18', 'HTML5', 'CSS3', 'JavaScript', 'jQuery',
        'Bootstrap', 'Kendo JS', 'TypeScript'
      ]
    },
    {
      id: 'cloud',
      name: 'Cloud & DevOps',
      icon: Cloud,
      color: 'text-secondary',
      skills: [
        'Azure CI/CD', 'Azure DevOps', 'Azure Functions', 'Azure Active Directory',
        'Docker', 'Kubernetes', 'GitHub Actions'
      ]
    },
    {
      id: 'database',
      name: 'Database',
      icon: Database,
      color: 'text-primary',
      skills: [
        'SQL Server', 'MySQL', 'MongoDB', 'SSIS', 'SSRS',
        'Entity Framework', 'Oracle'
      ]
    },
    {
      id: 'tools',
      name: 'Tools',
      icon: Wrench,
      color: 'text-accent',
      skills: [
        'Visual Studio 2022', 'VS Code', 'SSMS', 'Power BI',
        'GIT', 'SVN', 'Azure DevOps', 'Kofax KTA'
      ]
    }
  ];

  const [activeTab, setActiveTab] = useState('all');

  return (
    <section id="skills" className="py-16 sm:py-20 lg:py-24 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Technical <span className="text-gradient-azure">Skills</span>
          </h2>
          <div className="w-20 h-1 bg-gradient-azure mx-auto rounded-full"></div>
        </div>

        {/* Skills Tabs */}
        <Tabs defaultValue="all" className="max-w-6xl mx-auto" onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 mb-8 sm:mb-12 h-auto gap-2">
            <TabsTrigger value="all" className="text-xs sm:text-sm">All Skills</TabsTrigger>
            {skillCategories.map((category) => (
              <TabsTrigger key={category.id} value={category.id} className="text-xs sm:text-sm">
                {category.name}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="all" className="space-y-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {skillCategories.map((category, index) => {
                const Icon = category.icon;
                return (
                  <Card
                    key={category.id}
                    className="glass-card border-border/50 hover:shadow-elegant transition-all duration-300 animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <CardHeader>
                      <CardTitle className="flex items-center text-lg sm:text-xl">
                        <div className={`w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mr-3 ${category.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        {category.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {category.skills.map((skill) => (
                          <Badge key={skill} variant="secondary" className="text-xs sm:text-sm">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {skillCategories.map((category) => {
            const Icon = category.icon;
            return (
              <TabsContent key={category.id} value={category.id}>
                <Card className="glass-card border-border/50 max-w-3xl mx-auto">
                  <CardHeader>
                    <CardTitle className="flex items-center text-2xl sm:text-3xl">
                      <div className={`w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mr-4 ${category.color}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      {category.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      {category.skills.map((skill, index) => (
                        <Badge
                          key={skill}
                          variant="secondary"
                          className="text-sm sm:text-base px-4 py-2 animate-fade-in-up"
                          style={{ animationDelay: `${index * 0.05}s` }}
                        >
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </section>
  );
};